// Localização: frontend/src/components/Management/LoteForm.js (versão 1.0.0 - Com Alocação)

import React, { useState, useMemo } from 'react';
import PrimaryButton from '../Common/PrimaryButton';
import Alert from '../UI/Alert'; // Assumindo que você tem este componente
import DropdownMenu from '../UI/DropdownMenu';
import { usePacienteList } from '../../hooks/usePacienteList'; // Importando o hook de Pacientes
import { UserCircle, ChevronDown } from 'lucide-react';
import { getPacienteFotoUrl } from '../../utils/imageUtils';

const LoteForm = ({ initialData, medicamentosList, onSubmit, onClose, isEditing }) => {
    // 💡 NOVO: Hook para buscar a lista de pacientes
    const { pacientes, isLoadingPacientes } = usePacienteList(); 
    
    // 💡 NOVO: Adicionamos paciente_id
    const [formData, setFormData] = useState({
        medicamento_id: initialData?.medicamento_id || '',
        lote: initialData?.lote || '',
        data_validade: initialData?.data_validade || '',
        quantidade_inicial: initialData?.quantidade_inicial || '',
        paciente_id: initialData?.paciente_id || '', // NOVO CAMPO
    });
    const [error, setError] = useState(null);
    
    // Cálculo da unidade de medida (sem alteração)
    const selectedMedicamento = medicamentosList.find(
        (med) => String(med.id) === String(formData.medicamento_id)
    );
    const unidadeMedida = selectedMedicamento ? selectedMedicamento.unidade_medida : '';

    const pacientesDisponiveis = useMemo(() => {
        // Opção para não alocar (uso geral). O value vazio ('') será tratado como NULL no Submit.
        const geralOption = { id: '', nome_completo: '0 - Estoque Geral (Não Alocado)' }; 
        // Verifica se pacientes é um array válido antes de usar o spread
        return [geralOption, ...(Array.isArray(pacientes) ? pacientes : [])];
    }, [pacientes]);
    
    // Função de tratamento de mudança
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // Função de tratamento de envio
    const handleSubmit = (e) => {
        e.preventDefault();
        setError(null);
        
        const quantidade = Number(formData.quantidade_inicial);
        
        if (!formData.medicamento_id || !formData.lote || !formData.data_validade || isNaN(quantidade) || quantidade <= 0) {
            setError('Todos os campos são obrigatórios e a quantidade inicial deve ser um número positivo.');
            return;
        }
        
        const validade = new Date(formData.data_validade);
        const hoje = new Date();
        hoje.setHours(0, 0, 0, 0); 
        
        if (validade <= hoje) {
             setError('A data de validade deve ser futura.');
             return;
        }

        // 💡 Ajusta paciente_id: converte para null se for string vazia (Uso Geral)
        const pacienteIdToSubmit = formData.paciente_id ? parseInt(formData.paciente_id, 10) : null;

        onSubmit({
            ...formData,
            quantidade_inicial: quantidade,
            paciente_id: pacienteIdToSubmit, // Envia null ou o ID numérico
        });
    };

    return (
        <form onSubmit={handleSubmit} className="p-4">
            <h3 className="text-lg font-bold mb-4">Cadastrar Novo Lote</h3>
            {error && <Alert type="error" message={error} className="mb-4" />} {/* Usando o componente Alert.js */}
            
            <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700">Medicamento</label>
                <select
                    name="medicamento_id"
                    value={formData.medicamento_id}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                    required
                    disabled={isEditing} 
                >
                    <option value="">Selecione um medicamento</option>
                    {medicamentosList.map(med => (
                        <option key={med.id} value={med.id}>{med.nome_medicamento} ({med.dosagem}{med.unidade_medida})</option>
                    ))}
                </select>
                {medicamentosList.length === 0 && (
                     <p className="text-xs text-red-500 mt-1">Nenhum medicamento cadastrado. Cadastre um primeiro na aba anterior.</p>
                )}
            </div>

            {/* 💡 NOVO: CAMPO DE ALOCAÇÃO DE PACIENTE COM DROPDOWNMENU */}
            <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Alocar para Paciente (Opcional)
                </label>
                <DropdownMenu
                    trigger={
                        <button
                            type="button"
                            disabled={isLoadingPacientes || isEditing}
                            className="mt-1 w-full flex items-center justify-between px-3 py-2 text-left bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        >
                            <div className="flex items-center gap-2">
                                {(() => {
                                    const selectedPaciente = pacientesDisponiveis.find(p => String(p.id) === String(formData.paciente_id || ''));
                                    if (selectedPaciente && selectedPaciente.id) {
                                        const paciente = pacientes.find(p => p.id === selectedPaciente.id);
                                        return paciente?.foto ? (
                                            <img
                                                src={getPacienteFotoUrl(paciente.foto)}
                                                alt={selectedPaciente.nome_completo}
                                                className="h-5 w-5 rounded-full object-cover border border-gray-200"
                                            />
                                        ) : (
                                            <UserCircle size={20} className="text-gray-400" />
                                        );
                                    }
                                    return <UserCircle size={20} className="text-gray-400" />;
                                })()}
                                <span className="text-gray-700">
                                    {isLoadingPacientes 
                                        ? 'Carregando pacientes...' 
                                        : (() => {
                                            const selectedPaciente = pacientesDisponiveis.find(p => String(p.id) === String(formData.paciente_id || ''));
                                            return selectedPaciente ? selectedPaciente.nome_completo : 'Selecione um paciente ou deixe em Estoque Geral';
                                        })()
                                    }
                                </span>
                            </div>
                            <ChevronDown size={20} className="text-gray-400" />
                        </button>
                    }
                    items={pacientesDisponiveis.map(paciente => {
                        const pacienteCompleto = pacientes.find(p => p.id === paciente.id);
                        return {
                            id: paciente.id || 'geral',
                            label: paciente.nome_completo,
                            image: pacienteCompleto?.foto ? getPacienteFotoUrl(pacienteCompleto.foto) : null,
                            icon: !pacienteCompleto?.foto ? UserCircle : null,
                            iconColor: 'text-gray-400',
                            onClick: () => {
                                setFormData(prev => ({ ...prev, paciente_id: paciente.id || '' }));
                            },
                            className: String(formData.paciente_id || '') === String(paciente.id || '') ? 'bg-emerald-50' : ''
                        };
                    })}
                    position="bottom-left"
                />
                <p className="mt-1 text-xs text-gray-500">
                    Selecione um paciente para reservar este lote. Caso contrário, será considerado "Estoque Geral".
                </p>
            </div>


            <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700">Número do Lote</label>
                    <input
                        type="text"
                        name="lote"
                        value={formData.lote}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                        required
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Data de Validade</label>
                    <input
                        type="date"
                        name="data_validade"
                        value={formData.data_validade}
                        onChange={handleChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                        required
                    />
                </div>
            </div>

            <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700">
                    Quantidade Inicial <span className="text-gray-500 text-xs">({unidadeMedida || 'Unidade'})</span>
                </label>
                <input
                    type="number"
                    name="quantidade_inicial"
                    value={formData.quantidade_inicial}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                    required
                    min="1"
                />
            </div>
            
            <div className="flex justify-end space-x-2 mt-6">
                <PrimaryButton type="button" onClick={onClose} className="bg-gray-500 hover:bg-gray-700">Cancelar</PrimaryButton>
                <PrimaryButton type="submit">Cadastrar Lote</PrimaryButton>
            </div>
        </form>
    );
};

export default LoteForm;